import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export async function fetchProducts(params = {}) {
    const { data } = await api.get("/products", { params });
    return data.products;
}

/** Lean variant of fetchProducts — strips detail-page-only fields (~5x smaller payload).
 *  Use this on grid surfaces (Shop, Home, Featured, RecentlyViewed). The product detail
 *  page should keep using fetchProduct() which returns the full record. */
export async function fetchProductsLite(params = {}) {
    const { data } = await api.get("/products/lite", { params });
    return data.products;
}

export async function fetchProduct(slug) {
    const { data } = await api.get(`/products/${slug}`);
    return data;
}

export async function fetchCurrencyRate() {
    const { data } = await api.get("/currency/rate");
    return data;
}

export async function createCheckoutSession(payload) {
    const { data } = await api.post("/checkout/session", payload);
    return data;
}

export async function getCheckoutStatus(sessionId) {
    const { data } = await api.get(`/checkout/status/${sessionId}`);
    return data;
}

export async function previewBundle(items, opts = {}) {
    const params = new URLSearchParams();
    if (opts.email) params.set("email", opts.email);
    if (opts.firstOrderHint !== undefined) params.set("first_order_hint", String(opts.firstOrderHint));
    const { data } = await api.post(`/bundle/preview${params.toString() ? "?" + params.toString() : ""}`, items);
    return data;
}

export async function checkFirstOrder(email) {
    const { data } = await api.post("/checkout/first-order-check", { email });
    return data;
}

export async function adminCreateLabel(token, orderId, body = {}) {
    const { data } = await api.post(`/admin/orders/${orderId}/create-label`, body, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminSystemStatus(token) {
    const { data } = await api.get("/admin/system-status", { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminShippoSmokeTest(token) {
    const { data } = await api.post("/admin/shippo/smoke-test", {}, { headers: { "X-Admin-Token": token }, timeout: 60000 });
    return data;
}

export async function adminClearMoleculeOverride(token, productId) {
    const { data } = await api.delete(`/admin/products/${productId}/molecule-override`, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminListDiscountCodes(token) {
    const { data } = await api.get("/admin/discount-codes", { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminUpsertDiscountCode(token, body) {
    const { data } = await api.post("/admin/discount-codes", body, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminDeleteDiscountCode(token, code) {
    const { data } = await api.delete(`/admin/discount-codes/${encodeURIComponent(code)}`, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function validateDiscountCode(code, subtotalUsd) {
    const { data } = await api.post("/discount/validate", { code, subtotal_usd: subtotalUsd });
    return data;
}

export async function fetchShippingRates(payload) {
    // Uses live Shippo rates with flat-rate fallback
    const { data } = await api.post("/shipping/live-rates", payload);
    return data;
}

// Admin
export async function adminLogin(password) {
    const { data } = await api.post("/admin/auth", { password });
    return data;
}

export async function adminListProducts(token) {
    const { data } = await api.get("/admin/products", { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminPatchProduct(token, productId, patch) {
    const { data } = await api.patch(`/admin/products/${productId}`, patch, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminClearOverride(token, productId) {
    const { data } = await api.delete(`/admin/products/${productId}/override`, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function createNowPaymentsInvoice(payload) {
    const { data } = await api.post("/nowpayments/invoice", payload);
    return data;
}

export async function adminListOrders(token) {
    const { data } = await api.get("/admin/orders", { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminListProductsFull(token) {
    const { data } = await api.get("/admin/products/full", { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminPatchProductFull(token, productId, patch) {
    const { data } = await api.patch(`/admin/products/${productId}/full`, patch, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminUploadImage(token, file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function adminGetSettings(token) {
    const { data } = await api.get("/admin/settings", { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminPatchSettings(token, patch) {
    const { data } = await api.patch("/admin/settings", patch, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminDeleteProduct(token, productId) {
    const { data } = await api.delete(`/admin/products/${productId}`, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminRestoreProduct(token, productId) {
    const { data } = await api.post(`/admin/products/${productId}/restore`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminSendTracking(token, orderId, body) {
    const { data } = await api.post(`/admin/orders/${orderId}/send-tracking`, body, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminCreateProduct(token, body) {
    const { data } = await api.post("/admin/products/create", body, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminTestEmail(token, to) {
    const { data } = await api.post("/admin/test-email", { to }, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function fetchSocials() {
    const { data } = await api.get("/site/socials");
    return data;
}

export async function fetchSiteConfig() {
    const { data } = await api.get("/site/config");
    return data;
}

export async function lookupOrder(payload) {
    const { data } = await api.post("/orders/lookup", payload);
    return data;
}

export async function adminBroadcastRestock(token, productId) {
    const { data } = await api.post(`/admin/products/${productId}/broadcast-restock`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminDeleteOrder(token, orderId) {
    const { data } = await api.delete(`/admin/orders/${orderId}`, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminClearAllOrders(token) {
    const { data } = await api.post("/admin/orders/clear-all?confirm=YES", {}, { headers: { "X-Admin-Token": token } });
    return data;
}

export async function adminVerifyPayment(token, orderId) {
    const { data } = await api.post(`/admin/orders/${orderId}/verify-payment`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminMarkPaid(token, orderId) {
    const { data } = await api.post(`/admin/orders/${orderId}/mark-paid`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminMarkDelivered(token, orderId) {
    const { data } = await api.post(`/admin/orders/${orderId}/mark-delivered`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminMarkCompleted(token, orderId) {
    const { data } = await api.post(`/admin/orders/${orderId}/mark-completed`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminSendRecoveryEmail(token, orderId, force = false) {
    const { data } = await api.post(`/admin/orders/${orderId}/send-recovery${force ? "?force=true" : ""}`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminOrderDiagnostics(token, orderId) {
    const { data } = await api.get(`/admin/orders/${orderId}/diagnostics`, { headers: { "X-Admin-Token": token } });
    return data;
}

// ============ RESTRICTED CATALOG (members-only AAS section) ============
const RESTRICTED_TOKEN_KEY = "helixora_restricted_token";

export function getRestrictedToken() {
    return localStorage.getItem(RESTRICTED_TOKEN_KEY);
}
export function setRestrictedToken(token) {
    if (token) localStorage.setItem(RESTRICTED_TOKEN_KEY, token);
    else localStorage.removeItem(RESTRICTED_TOKEN_KEY);
}

function restrictedHeaders() {
    const t = getRestrictedToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function submitRestrictedAccessRequest(payload) {
    const { data } = await api.post("/restricted/access-request", payload);
    return data;
}
export async function restrictedLogin(identifier, password) {
    const { data } = await api.post("/restricted/auth/login", { identifier, password });
    return data;
}
export async function restrictedMe() {
    const { data } = await api.get("/restricted/auth/me", { headers: restrictedHeaders() });
    return data;
}
export async function fetchRestrictedProducts() {
    const { data } = await api.get("/restricted/products", { headers: restrictedHeaders() });
    return data.products;
}
export async function fetchRestrictedProduct(slug) {
    const { data } = await api.get(`/restricted/products/${slug}`, { headers: restrictedHeaders() });
    return data;
}

// Admin
export async function adminListRestrictedRequests(token, status) {
    const params = status ? `?status=${status}` : "";
    const { data } = await api.get(`/admin/restricted/access-requests${params}`, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminApproveRestrictedRequest(token, reqId) {
    const { data } = await api.post(`/admin/restricted/access-requests/${reqId}/approve`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminDenyRestrictedRequest(token, reqId) {
    const { data } = await api.post(`/admin/restricted/access-requests/${reqId}/deny`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminDeleteRestrictedRequest(token, reqId) {
    const { data } = await api.delete(`/admin/restricted/access-requests/${reqId}`, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminListRestrictedUsers(token) {
    const { data } = await api.get(`/admin/restricted/users`, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminCreateRestrictedUser(token, body) {
    const { data } = await api.post(`/admin/restricted/users`, body, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminResetRestrictedUserPassword(token, userId) {
    const { data } = await api.post(`/admin/restricted/users/${userId}/reset-password`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminToggleRestrictedUser(token, userId) {
    const { data } = await api.patch(`/admin/restricted/users/${userId}/toggle`, {}, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminDeleteRestrictedUser(token, userId) {
    const { data } = await api.delete(`/admin/restricted/users/${userId}`, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminListRestrictedProducts(token) {
    const { data } = await api.get(`/admin/restricted/products`, { headers: { "X-Admin-Token": token } });
    return data;
}
export async function adminPatchRestrictedProduct(token, productId, patch) {
    const { data } = await api.patch(`/admin/restricted/products/${productId}`, patch, { headers: { "X-Admin-Token": token } });
    return data;
}
